import { SUCCESS_MESSAGES, getFullName } from '@zorro/common-utils';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { SubEntityVariant } from '@zorro/types';
import { mapEmployeeForActions } from '@zorro/web/shared-ui';
import {
  useAppRouter,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import {
  AddEnrollmentRecordForm,
  DeleteEnrollmentRecord,
  EditEmployeeRoleForm,
  EditEnrollmentsRecord,
  EmployeeEmailForm,
  EmployeeLeaveOfAbsenceForm,
  TerminateEmployeeForm,
  UpdateEligibilityForm,
} from '../EmployeeActions';
import { DeleteEmployee } from '../EmployeeActions/AccountManagement/DeleteEmployee';
import { AssignEnrollmentForm } from '../EmployeeActions/EnrollmentActions/AssignEnrollmentForm';
import { EditIdFromEmployerForm } from './EmployeeLayoutSidebar/EditIdFromEmployerForm';
import { EditSubEntityForm } from './EmployeeLayoutSidebar/EditSubEntityForm';
import { getEmployeeRole } from './EmployeeLayoutUtils';

export enum EmployeeLayoutAction {
  EDIT_COMPANY_EMAIL = 'EDIT_COMPANY_EMAIL',
  DELETE_EMPLOYEE = 'DELETE_EMPLOYEE',
  TERMINATE_EMPLOYEE = 'TERMINATE_EMPLOYEE',
  SET_LEAVE_OF_ABSENCE = 'SET_LEAVE_OF_ABSENCE',
  UPDATE_ELIGIBILITY = 'UPDATE_ELIGIBILITY',
  EDIT_EMPLOYEE_ROLE = 'EDIT_EMPLOYEE_ROLE',
  EDIT_ID_FROM_EMPLOYER = 'EDIT_ID_FROM_EMPLOYER',
  EDIT_SUB_ENTITY = 'EDIT_SUB_ENTITY',
  EDIT_SUB_ENTITY_2 = 'EDIT_SUB_ENTITY_2',
  ADD_ENROLLMENT = 'ADD_ENROLLMENT',
  EDIT_ENROLLMENT = 'EDIT_ENROLLMENT',
  ASSIGN_ENROLLMENT = 'ASSIGN_ENROLLMENT',
  DELETE_ENROLLMENT = 'DELETE_ENROLLMENT',
  EMAIL_ACCOUNT_INVITATION = 'EMAIL_ACCOUNT_INVITATION',
}

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId?: string;
  action?: EmployeeLayoutAction;
  setAction: (action?: EmployeeLayoutAction) => void;
  onChangeOnboardingPeriod?: () => Promise<void>;
};

export const EmployeeLayoutActions = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  action,
  setAction,
  onChangeOnboardingPeriod,
}: Props) => {
  const { navigateToRoute } = useAppRouter();
  const { tryMutate: sendActivationEmails } = useMonolithMutation({
    method: 'usersControllerSendActivationEmails',
    successMessage: SUCCESS_MESSAGES.getEmailSentSuccessfullyMessage(1),
  });
  const { data: employee, isLoading: isLoadingEmployee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { data: userRoles, isLoading: isLoadingRoles } = useMonolithQuery({
    method: 'usersControllerGetUserRoles',
    params: employee && [employee.userId],
  });

  if (!employee || !userRoles || isLoadingEmployee || isLoadingRoles) {
    return null;
  }

  const role = getEmployeeRole(userRoles);
  const recordForActions = mapEmployeeForActions(
    employee,
    onboardingPeriodId,
    undefined,
    role
  );

  const sendActivationEmail = async () => {
    await sendActivationEmails([
      [
        {
          id: recordForActions.userId,
          tenantId: recordForActions.employerId,
        },
      ],
    ]);
    handleCloseAction();
  };

  const handleCloseAction = () => setAction(undefined);

  return (
    <>
      <Drawer
        opened={action === EmployeeLayoutAction.EDIT_COMPANY_EMAIL}
        onClose={handleCloseAction}
        title="Edit company email"
      >
        <EmployeeEmailForm
          onSuccess={handleCloseAction}
          onCancel={handleCloseAction}
          employeeId={employeeId}
        />
      </Drawer>

      <DeleteEmployee
        isOpen={action === EmployeeLayoutAction.DELETE_EMPLOYEE}
        onClose={handleCloseAction}
        employerId={employerId}
        employeeId={employeeId}
      />

      <Drawer
        onClose={handleCloseAction}
        title="Terminate Employee"
        opened={action === EmployeeLayoutAction.TERMINATE_EMPLOYEE}
      >
        <TerminateEmployeeForm
          recordsForActions={[recordForActions]}
          onSuccess={async () => {
            handleCloseAction();
            await onChangeOnboardingPeriod?.();
          }}
        />
      </Drawer>

      <Drawer
        onClose={handleCloseAction}
        title="Set a leave of absence"
        opened={action === EmployeeLayoutAction.SET_LEAVE_OF_ABSENCE}
      >
        <EmployeeLeaveOfAbsenceForm
          recordsForActions={[recordForActions]}
          onSuccess={handleCloseAction}
        />
      </Drawer>

      <Drawer
        onClose={handleCloseAction}
        title="Update eligibility"
        opened={action === EmployeeLayoutAction.UPDATE_ELIGIBILITY}
      >
        <UpdateEligibilityForm
          recordsForActions={[recordForActions]}
          onSuccess={async () => {
            handleCloseAction();
            await onChangeOnboardingPeriod?.();
          }}
        />
      </Drawer>

      <Drawer
        opened={action === EmployeeLayoutAction.EDIT_EMPLOYEE_ROLE}
        title="Change employee role"
        onClose={handleCloseAction}
      >
        <EditEmployeeRoleForm
          recordsForActions={[recordForActions]}
          onSuccess={handleCloseAction}
        />
      </Drawer>

      <Drawer
        opened={action === EmployeeLayoutAction.EDIT_ID_FROM_EMPLOYER}
        title="Change employee ID"
        onClose={handleCloseAction}
      >
        <EditIdFromEmployerForm
          onClose={handleCloseAction}
          employeeId={employeeId}
        />
      </Drawer>

      <Drawer
        opened={action === EmployeeLayoutAction.EDIT_SUB_ENTITY}
        title="Change employee sub-entity 1"
        onClose={handleCloseAction}
      >
        <EditSubEntityForm
          onClose={handleCloseAction}
          employeeId={employeeId}
          employerId={employerId}
          type={SubEntityVariant.subEntity}
        />
      </Drawer>

      <Drawer
        opened={action === EmployeeLayoutAction.EDIT_SUB_ENTITY_2}
        title="Change employee sub-entity 2"
        onClose={handleCloseAction}
      >
        <EditSubEntityForm
          onClose={handleCloseAction}
          employeeId={employeeId}
          employerId={employerId}
          type={SubEntityVariant.subEntity2}
        />
      </Drawer>

      <Drawer
        opened={action === EmployeeLayoutAction.ADD_ENROLLMENT}
        title="Add enrollment record"
        onClose={handleCloseAction}
      >
        <AddEnrollmentRecordForm
          employerId={employerId}
          onSuccess={(onboardingPeriodId) => {
            setAction(undefined);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            onboardingPeriodId
              ? navigateToRoute('OPS_EMPLOYEE_PLANS_TAB', [
                  {
                    employerId,
                    employeeId,
                    onboardingPeriodId,
                  },
                ])
              : navigateToRoute('OPS_EMPLOYEE_PAGE', [
                  { employerId, employeeId },
                ]);
          }}
          recordsForActions={[recordForActions]}
        />
      </Drawer>

      {onboardingPeriodId && (
        <Drawer
          opened={action === EmployeeLayoutAction.EDIT_ENROLLMENT}
          title="Edit enrollment record"
          onClose={handleCloseAction}
        >
          <EditEnrollmentsRecord
            onSuccess={handleCloseAction}
            employerId={employerId}
            recordsForActions={[recordForActions]}
          />
        </Drawer>
      )}

      {onboardingPeriodId && (
        <Drawer
          opened={action === EmployeeLayoutAction.ASSIGN_ENROLLMENT}
          title="Assign enrollment"
          onClose={handleCloseAction}
        >
          <AssignEnrollmentForm
            onClose={handleCloseAction}
            employerId={employerId}
            recordsForActions={[recordForActions]}
          />
        </Drawer>
      )}

      <DeleteEnrollmentRecord
        isOpen={action === EmployeeLayoutAction.DELETE_ENROLLMENT}
        onSubmit={() =>
          navigateToRoute(
            'OPS_EMPLOYEE_PAGE',
            [{ employerId, employeeId }],
            {},
            false,
            false
          )
        }
        onboardingPeriodId={onboardingPeriodId}
        onClose={handleCloseAction}
      />

      <ModalMessage
        isOpen={action === EmployeeLayoutAction.EMAIL_ACCOUNT_INVITATION}
        onClose={handleCloseAction}
        title={`Send account invitation email to ${getFullName(employee)}?`}
        description="Are you sure you want to send this email to the selected employee?"
        primaryLabel="Send email"
        onPrimaryAction={sendActivationEmail}
        secondaryLabel="Cancel"
        onSecondaryAction={handleCloseAction}
      />
    </>
  );
};
